import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, TimeoutError } from 'rxjs';
import { takeUntil, timeout } from 'rxjs/operators';

import { UtilService } from 'src/@sbt/utils/util.service';
import { GlobalService } from 'src/app/app-config/global.service';
import { loginCredential } from '../types/type';
import { LoadingController, NavController, Platform } from '@ionic/angular';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  baseUrl: string;
  maxTime: number = 10000;

  constructor(
    private router: Router,
    private httpClient: HttpClient,
    private global: GlobalService,
    private util: UtilService,
    private navController: NavController,
    private loadingController: LoadingController,
    private platform: Platform) { 
      this.baseUrl = this.global.baseUrl;
    }
  
  // Generic API
  httpGet(resource: string, maxTime: number = this.maxTime): Observable<any> {    
    return this.httpClient.get(this.baseUrl + 'generic/' + resource).pipe(      
      timeout(maxTime)
    );
  }

  httpGetNonGeneric(resource: string, maxTime: number = this.maxTime): Observable<any> {    
    return this.httpClient.get(this.baseUrl + resource).pipe(      
      timeout(maxTime)
    );
  }

  httpGetFile(resource: string, maxTime: number = this.maxTime): Observable<any> {    
    return this.httpClient.get(this.global.fileUrl + resource).pipe(      
      timeout(maxTime)
    );
  }
  
  httpGetById(id, resource: string, maxTime: number = this.maxTime): Observable<any> {
    resource = `${resource}?offset=0&fetch=1&where=Id,=,${id}`;
    return this.httpClient.get(this.baseUrl + 'generic/' + resource).pipe(      
      timeout(maxTime)
    );
  }

  httpPut(id, resource: string, data: any, maxTime: number = this.maxTime): Observable<any> {
    resource = `${resource}?Id=${id}`;
    return this.httpClient.put(this.baseUrl + 'generic/' + resource, data).pipe(      
      timeout(maxTime)
    );
  }

  httpPost(resource: string, data: any, maxTime: number = this.maxTime): Observable<any> {        
    return this.httpClient.post(this.baseUrl + 'generic/' + resource, data).pipe(      
      timeout(maxTime)
    );
  }

  httpPostNonGeneric(resource: string, data: any, maxTime: number = this.maxTime): Observable<any> {        
    return this.httpClient.post(resource, data).pipe(      
      timeout(maxTime)
    );
  }

  httpDelete(id, resource: string, maxTime: number = this.maxTime): Observable<any> {       
    resource = `${resource}?Id=${id}`;
    return this.httpClient.delete(this.baseUrl + 'generic/' + resource).pipe(      
      timeout(maxTime)
    );
  }

  // Lookup Api
  lookupTable(tableName: string, destroy$: any, dataContainer: any, maxTime: number = this.maxTime) {
    this.httpClient.get(this.baseUrl + `lookup/table/${tableName}`)
      .pipe(timeout(maxTime), takeUntil(destroy$))
      .subscribe((data) => {
        dataContainer = data;
      }, error => {
        this.handleApiError(error);
      });
  }

  lookupDropbox(dropbox: string, destroy$: any, dataContainer: any, maxTime: number = this.maxTime) {
    this.httpClient.get(this.baseUrl + `lookup/dropbox/${dropbox}`)
      .pipe(timeout(maxTime), takeUntil(destroy$))
      .subscribe((data) => {
        dataContainer = data;
      }, error => {
        this.handleApiError(error);
      });
  }

  lookupSP(sp: string, destroy$: any, data: any, dataContainer: any, maxTime: number = this.maxTime) {
    this.httpClient.post(this.baseUrl + `action/${sp}`, data)
      .pipe(timeout(maxTime), takeUntil(destroy$))
      .subscribe((data) => {
        dataContainer = data;
      }, error => {
        this.handleApiError(error);
      });
  }

  // Security Api
  httpPostSecurity(resource: string, data: any, maxTime: number = this.maxTime): Observable<any> {        
    return this.httpClient.post(this.baseUrl + 'security/' + resource, data).pipe(      
      timeout(maxTime)
    );
  }

  httpGetSecurity(resource: string, maxTime: number = this.maxTime): Observable<any> {        
    return this.httpClient.get(this.baseUrl + 'security/' + resource).pipe(      
      timeout(maxTime)
    );
  }

  // File Api
  httpUpload(formData: FormData, queryParam: string) {
    return this.httpClient.post(`${this.global.fileUrl}upload${queryParam}`, formData);
  }

  httpSas(containerName: string) {
    return this.httpClient.get(`${this.global.fileUrl}sas/${containerName}`);
  }

  // Generic Action Api
  httpAction(resource: string, data?: any, maxTime: number = this.maxTime): Observable<any> {        
    return this.httpClient.post(this.baseUrl + 'action/' + resource, data).pipe(      
      timeout(maxTime)
    );
  }

  httpAction2(resource: string, data?: any, maxTime: number = this.maxTime): Observable<any> {        
    return this.httpClient.post(this.baseUrl + 'action2/' + resource, data).pipe(      
      timeout(maxTime)
    );
  }

  httpWorkflow(resource: string, data?: any, maxTime: number = this.maxTime): Observable<any> {        
    return this.httpClient.post(this.baseUrl + 'workflow/' + resource, data).pipe(      
      timeout(maxTime)
    );
  }

  async handleApiError(error: HttpErrorResponse, callback: () => void = () => {}) {
    if (!navigator.onLine) {
      this.util.genericMessageBox('ClientAlert', 'ErrorInternetDown');
      callback();
      return;
    }

    if (error instanceof TimeoutError) {
      callback();
      this.util.genericMessageBox('ClientAlert', 'ErrorTimeOut');
      return;
    }

    if (error instanceof ErrorEvent) {
      this.util.genericMessageBox('ClientAlert', 'ErrorData');
      callback();
      return;
    }

    if (error.status === 400) {
      this.util.genericMessageBox('AdministratorAlert', 'Error400');
      callback();
      return;
    }

    if (error.status === 401) {
      callback();
      this.global.accessToken = undefined;
      this.router.navigate(['/login']);
      return;
    }

    if (error.status === 403) {
      this.util.genericMessageBox('ClientAlert', 'ErrorIPX');
      callback();
      return;
    }

    if (error.status === 500) {
      this.util.genericMessageBox('AdministratorAlert', 'Error500');
      callback();
      return;
    }

    if (error.error !== null) {
      if (error.error.hasOwnProperty('text')) {
        switch (error.error.text) {  
          case ('DPK'):
            this.util.genericMessageBox('ClientAlert', 'ErrorDPX');
            callback();
            return;
          case ('MPX'):
            this.util.genericMessageBox('ClientAlert', 'ErrorMPX');
            callback();
            return;
          case ('RIX'):
            this.util.genericMessageBox('ClientAlert', 'ErrorRIX');
            callback();
            return;
          case ('IDX'):
            this.util.genericMessageBox('ClientAlert', 'ErrorIDX');
            callback();
            return;
          case ('FPX'):
            this.util.genericMessageBox('ClientAlert', 'ErrorFPX');
            callback();
            return;
          case ('JVX'):
            this.util.genericMessageBox('ClientAlert', 'ErrorJVX');
            callback();
            return;
          case ('RFX'):
            this.util.genericMessageBox('ClientAlert', 'ErrorRFX');
            callback();
            return;
          case ('RVX'):
            this.util.genericMessageBox('ClientAlert', 'ErrorRVX');
            callback();
            return;
          case ('OTP-E10'):
            this.util.genericMessageBox('ClientAlert', 'OTP-E10');
            callback();
            return;
        }
      }
    }
    this.util.genericMessageBox('AdministratorAlert', error.message);
    callback();
  }
}